import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { Alert, Card, CardContent } from '@guildeducationinc/recess';
import { ApprovalStatusTag } from '@src/components/ApprovalStatusTag';
import { CLAIMS_PROCESSOR_APPROVALS } from '@src/graphql/queries/approvals';
import { useAuth } from '@src/hooks/useAuth';
import { WorkflowRunTaskStatus } from '@src/types';
import { camelCase, compact } from 'lodash';
import { DataTable, TrackableButton, useLocale, usePaginatedFetchItems, useSessionState, } from 'pe-core-frontend';
import { useMemo } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { ExceptionRequestsAdminTopNav } from '../ExceptionRequestsAdminTopNav';
export var ExceptionRequestsTab;
(function (ExceptionRequestsTab) {
    ExceptionRequestsTab["PENDING"] = "pending";
    ExceptionRequestsTab["INFO_REQUESTED"] = "info_requested";
    ExceptionRequestsTab["PROCESSED"] = "processed";
})(ExceptionRequestsTab || (ExceptionRequestsTab = {}));
export const ExceptionRequestsList = () => {
    const { t, formatToLocalDatetime } = useLocale();
    const navigate = useNavigate();
    const { tab = ExceptionRequestsTab.PENDING } = useParams();
    const { isSuperuser, isClaimsProcessor } = useAuth();
    const selectedTab = tab.toLowerCase();
    const [filter, setFilter] = useSessionState(`exceptionRequestFilter_${tab}`, {});
    const spec = useMemo(() => {
        const workflowStatuses = [];
        switch (selectedTab) {
            case ExceptionRequestsTab.PENDING:
                workflowStatuses.push(WorkflowRunTaskStatus.PENDING, WorkflowRunTaskStatus.IN_PROGRESS);
                break;
            case ExceptionRequestsTab.INFO_REQUESTED:
                workflowStatuses.push(WorkflowRunTaskStatus.INFO_REQUESTED);
                break;
            case ExceptionRequestsTab.PROCESSED:
                workflowStatuses.push(WorkflowRunTaskStatus.APPROVED, WorkflowRunTaskStatus.CLOSED, WorkflowRunTaskStatus.REJECTED, WorkflowRunTaskStatus.CANCELLED, WorkflowRunTaskStatus.EXPIRED);
                break;
        }
        return {
            onlyPending: false,
            clientId: null,
            workflowStatuses,
            ...filter,
        };
    }, [filter, selectedTab]);
    const { page, setPage, pageSize, setPageSize, total, items, loading, error, orderBy, setOrderBy, orderByDir, setOrderByDir, } = usePaginatedFetchItems({
        spec,
        fetchQuery: CLAIMS_PROCESSOR_APPROVALS,
        dataProp: 'claimsProcessorApprovals',
        initialPageSize: 10,
    });
    const handleFilterChange = (newFilter) => {
        setFilter(newFilter);
        setPage(1);
    };
    const handleSortModelChange = (sortModel) => {
        if (sortModel && sortModel.length > 0) {
            const sort = sortModel[0];
            setOrderBy(sort.field);
            setOrderByDir(sort.sort);
        }
        else {
            setOrderBy(null);
            setOrderByDir(null);
        }
    };
    const columns = compact([
        {
            field: 'requestNum',
            headerName: t('Request #'),
            width: 110,
            flex: 1.1,
        },
        {
            field: 'employeeName',
            headerName: t('Employee Name'),
            width: 200,
            flex: 2,
            sortable: false,
        },
        {
            field: 'employeeId',
            headerName: t('Employee ID'),
            width: 140,
            flex: 1.4,
            sortable: false,
            renderCell: ({ row }) => row.employeeId,
        },
        {
            field: 'clientName',
            headerName: t('Employer'),
            width: 150,
            flex: 1.5,
            sortable: false,
            renderCell: ({ row }) => row.clientName,
        },
        {
            field: 'submittedAt',
            headerName: t('Submitted'),
            width: 140,
            flex: 1.4,
            sortable: true,
            renderCell: ({ value }) => formatToLocalDatetime(value, { dateStyle: 'medium' }),
        },
        {
            field: 'workflowType',
            headerName: t('Type'),
            width: 200,
            flex: 2,
            sortable: false,
            renderCell: ({ row }) => t(`WorkflowType.${camelCase(row.workflowType)}`),
        },
        [ExceptionRequestsTab.PENDING].includes(tab) && {
            field: 'status',
            headerName: t('Status'),
            width: 150,
            flex: 1.5,
            sortable: false,
            renderCell: ({ row }) => _jsx(ApprovalStatusTag, { approval: row, pending: true }),
        },
    ]);
    columns.push({
        field: 'actions',
        headerName: t('Actions'),
        width: 180,
        flex: 1.8,
        sortable: false,
        renderCell: ({ row }) => (_jsx("div", { className: 'p-1 flex gap-2 items-center', children: [ExceptionRequestsTab.PENDING].includes(tab) && isClaimsProcessor && (_jsx(TrackableButton, { btnType: 'mui', enText: 'Process', tag: 'exception_requests_list', variant: 'outlined', onClick: () => navigate(`/admin/exceptions/requests/${row.id}`), children: t('Process') })) })),
    });
    return (_jsxs("div", { children: [tab && (_jsx(ExceptionRequestsAdminTopNav, { tab: tab, showFilter: true, filter: filter, handleFilterChange: handleFilterChange, isSuperuser: isSuperuser })), _jsx("div", { children: _jsx(Card, { children: _jsxs(CardContent, { children: [error && (_jsx(Alert, { hideClose: true, display: true, type: 'danger', children: t('Failed to load exception requests') })), !error && total === 0 && (_jsx(Alert, { hideClose: true, display: true, type: 'info', children: t('No exception requests found') })), !error && total > 0 && (_jsx(DataTable, { disableColumnMenu: true, disableRowSelectionOnClick: true, rows: items, pageSizeOptions: [10, 25], paginationModel: { page: page - 1, pageSize }, paginationMode: 'server', onPaginationModelChange: pagination => {
                                    setPage(pagination.page + 1);
                                    setPageSize(pagination.pageSize);
                                }, loading: loading, rowCount: total, sortingMode: 'server', onSortModelChange: handleSortModelChange, sortModel: [
                                    {
                                        field: orderBy ?? 'createdAt',
                                        sort: orderByDir,
                                    },
                                ], columns: columns }))] }) }) })] }));
};
